import { AlertCircle, ArrowLeft, Loader2, Search } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { useSocket } from '../contexts/socketTypes';
import axios from 'axios';
import { AnalysisService } from '../services/analysisService';
import { analytics } from '../services/analytics/posthog';
import { logger } from '../utils/logger';

/**
 * QuickScanPage - Scan any public repository by URL
 *
 * Allows users to paste a GitHub, GitLab, or Bitbucket URL to analyze
 * any public repository without requiring OAuth connection.
 */
export const QuickScanPage: React.FC = () => {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [refType, setRefType] = useState<'branch' | 'tag' | 'commitSha' | ''>('');
  const [refValue, setRefValue] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analytics.pageViewed('quick_scan');
  }, []);

  // Validate URL format before submission
  const isValidUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url.trim());
      const hostname = parsed.hostname.toLowerCase();
      // Check for supported platforms
      return (
        hostname === 'github.com' ||
        hostname === 'www.github.com' ||
        hostname === 'gitlab.com' ||
        hostname === 'www.gitlab.com' ||
        hostname === 'bitbucket.org' ||
        hostname === 'www.bitbucket.org'
      );
    } catch {
      return false;
    }
  };

  // Extract platform from URL for display
  const getPlatformFromUrl = (url: string): string | null => {
    try {
      const parsed = new URL(url.trim());
      const hostname = parsed.hostname.toLowerCase();
      if (hostname.includes('github')) return 'github';
      if (hostname.includes('gitlab')) return 'gitlab';
      if (hostname.includes('bitbucket')) return 'bitbucket';
      return null;
    } catch {
      return null;
    }
  };

  const handleScan = async () => {
    if (!repositoryUrl.trim()) {
      setError('Please enter a repository URL');
      return;
    }

    if (!isValidUrl(repositoryUrl)) {
      setError('Please enter a valid GitHub or GitLab repository URL');
      return;
    }

    setIsScanning(true);
    setError(null);

    try {
      const response = await AnalysisService.scanUrl({
        repositoryUrl: repositoryUrl.trim(),
        socketId: socket?.id || '',
        refType: refType || undefined,
        refValue: refValue.trim() || undefined,
      });

      const result = response.data;

      if (result.jobId) {
        analytics.analysisStarted(
          repositoryUrl.trim(),
          getPlatformFromUrl(repositoryUrl) as 'github' | 'gitlab' | 'local',
        );
        // Navigate to job status page
        navigate(`/jobs/${result.jobId}`);
      } else {
        throw new Error('Failed to start scan - no job ID returned');
      }
    } catch (err: unknown) {
      logger.error('Scan error', { error: err });
      let errorMessage = 'Failed to start scan. Please check the URL and try again.';
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setIsScanning(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isScanning) {
      handleScan();
    }
  };

  const isUrlValid = repositoryUrl.trim() && isValidUrl(repositoryUrl);

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-200 px-4 sm:px-6 lg:px-8">
      {/* Common Header */}
      <Navbar pageTitle="Analyze a Repository" showDivider={true} />

      {/* Main Content */}
      <main className="grow mt-16">
        <div className="max-w-7xl mx-auto py-8">
          {/* Back Button */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 mb-6 text-sm font-medium transition-colors duration-200"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          {/* Centered Content Container */}
          <div className="max-w-3xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Analyze Any Public Repository
              </h1>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Enter a public GitHub or GitLab URL
              </p>
            </div>

            {/* URL Input Card */}
            <div
              className="rounded-lg p-6 mb-6"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-default)',
              }}
            >
              {/* URL Input */}
              <div className="relative">
                <input
                  type="url"
                  value={repositoryUrl}
                  onChange={(e) => {
                    setRepositoryUrl(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="https://github.com/owner/repository"
                  className="w-full px-4 py-3 pr-12 rounded-lg text-base transition-colors duration-200"
                  style={{
                    border: error
                      ? '2px solid var(--color-error)'
                      : isUrlValid
                        ? '2px solid var(--color-accent-green)'
                        : '1px solid var(--color-border-default)',
                    color: 'var(--color-text-primary)',
                  }}
                  disabled={isScanning}
                  autoFocus
                />
                <div
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  <Search className="w-5 h-5" />
                </div>
              </div>

              {/* Optional ref selector */}
              <div className="flex gap-2 mt-3">
                <select
                  value={refType}
                  onChange={(e) => {
                    setRefType(e.target.value as 'branch' | 'tag' | 'commitSha' | '');
                    setRefValue('');
                  }}
                  disabled={isScanning}
                  className="w-auto px-3 py-2 rounded-lg text-sm transition-colors duration-200 shrink-0"
                  style={{
                    border: '1px solid var(--color-border-default)',
                    color: 'var(--color-text-primary)',
                    backgroundColor: 'var(--color-bg-primary)',
                  }}
                >
                  <option value="">Default branch</option>
                  <option value="branch">Branch</option>
                  <option value="tag">Tag</option>
                  <option value="commitSha">Commit</option>
                </select>
                {refType && (
                  <input
                    type="text"
                    value={refValue}
                    onChange={(e) => setRefValue(e.target.value)}
                    placeholder={
                      refType === 'commitSha'
                        ? 'e.g. a1b2c3d4...'
                        : refType === 'tag'
                          ? 'e.g. v1.0.0'
                          : 'e.g. develop'
                    }
                    disabled={isScanning}
                    className="flex-1 px-3 py-2 rounded-lg text-sm transition-colors duration-200"
                    style={{
                      border: '1px solid var(--color-border-default)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div
                  className="flex items-center gap-2 mt-3 text-sm"
                  style={{ color: 'var(--color-error)' }}
                >
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleScan}
                disabled={isScanning || !repositoryUrl.trim()}
                className="w-full mt-4 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isScanning
                    ? 'var(--color-accent-blue)'
                    : 'var(--color-accent-teal)',
                }}
              >
                {isScanning ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting Scan...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Search className="w-5 h-5" />
                    Analyze Now
                  </span>
                )}
              </button>
            </div>

            {/* Info Section */}
            <div
              className="rounded-lg p-4 text-sm"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                How it works
              </h3>
              <ul className="space-y-1 list-disc list-inside">
                <li>Analyze any public repository and produce insights</li>
                <li>Enter a public repository URL from GitHub or GitLab</li>
                <li>No OAuth connection required for public repositories</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default QuickScanPage;
